import $ from "../lib/jquery.js";
import { debounce } from "../lib/dom.js";
import { draggable } from "../lib/draggable.js";

/*
 * Pannable illustrated map with numbered markers.
 *
 * Two menu styles are supported, and you use one or the other:
 *   .interactive-map-menu-categories  a rail of category toggles
 *   .interactive-map-menu-rollover    a list whose links highlight a marker
 *
 * The original version needed jQuery UI draggable, touch-punch and qTip2.
 * None of those were ever vendored into the template, so panning is now done
 * with lib/draggable.js and tooltips reuse the #interactive-map-information
 * popup that the rollover menu already relied on.
 *
 * Set --interactive-map-width / --interactive-map-height in your page CSS to
 * match the artwork.
 */

/* Set to true on staging to drag markers into place and save their coords. */
const EDIT_MODE = false;

function positionContent($map, $content) {
  const isPhone = window.innerWidth <= 480;
  const startTop = isPhone ? -780 : -780;

  if ($map.hasClass("unconstrained")) {
    $map.width(window.innerWidth);
  }

  // Centre the artwork horizontally on the viewport.
  const left = ($content.width() - $map.width()) / -2;

  $content.css({ top: `${startTop}px`, left: `${left}px` });
}

function makePannable($map, $content) {
  const content = $content.get(0);

  draggable(content, {
    containment: () => ({
      left: $map.width() - $content.width(),
      top: $map.height() - $content.height(),
      right: 0,
      bottom: 0,
    }),
  });
}

function createInfoWindow($map) {
  const $info = $("#interactive-map-information");

  if (!$info.length) {
    return { show() {}, hide() {} };
  }

  const $arrow = $info.find(".interactive-map-information-arrow");
  const $arrowReversed = $info.find(".interactive-map-information-arrow-reversed");
  const $text = $info.find(".interactive-map-information-text");

  return {
    show(item) {
      const $marker = $(`#interactive-map-item-${item}`);
      const $source = $(`#interactive-menu-item-number-${item}`);

      if (!$marker.length) {
        return;
      }

      const title = ($source.data("title") ?? "").toString().replaceAll("<br />", " ");
      const top = parseInt($marker.css("top"), 10);
      const markerWidth = $marker.outerWidth();
      const markerHeight = $marker.outerHeight();

      let left = parseInt($marker.css("left"), 10);

      $text.html(title);

      // Flip the callout to the other side once the marker passes the midpoint,
      // so the popup never runs off the edge of the artwork.
      const flip = left > $map.find(".interactive-map-content").width() / 2;

      $arrow.toggle(!flip);
      $arrowReversed.toggle(flip);

      left = flip ? left - markerWidth * 2 - $text.outerWidth() : left + markerWidth;

      $info.css({ top: `${top - markerHeight / 4}px`, left: `${left}px` });
    },

    hide() {
      $info.css({ top: "-1000px" });
    },
  };
}

function bindCategoryMenu(infoWindow) {
  const $items = $(".interactive-map-menu-categories-item");

  if (!$items.length) {
    return;
  }

  $items.on("click", function toggleCategory() {
    infoWindow.hide();

    const $item = $(this);

    $(`.category-${$item.data("category")}`).toggle();
    $item.toggleClass("active");
  });

  // Collapsible header on the dropdown variant.
  const $header = $(".interactive-map-items-header");

  if ($(".interactive-map-dropdown-menu").length && $header.length) {
    const fullHeight = $header.height();

    $header.css({ height: 0, overflow: "hidden" });

    $(".interactive-map-menu-header").on("click", () => {
      $header.stop().animate({ height: $header.height() === 0 ? fullHeight : 0 }, 250);
    });
  }

  // Markers act as their own tooltips.
  $(".interactive-map-item").on("click", function showTip() {
    infoWindow.show($(this).data("offset") - 1);
  });
}

function bindRolloverMenu(infoWindow) {
  if (!$(".interactive-map-menu-rollover").length) {
    return;
  }

  // This variant shows every marker at once.
  $(".interactive-map-item").css("display", "block");

  $(".interactive-menu-rollover-item-link").on("mouseenter", function highlight() {
    infoWindow.show($(this).data("itemnumber"));
  });

  $(".interactive-map-item")
    .on("mouseenter click", function highlight() {
      infoWindow.show($(this).data("offset") - 1);
    })
    .on("mouseleave", () => {
      // On touch devices the popup should stay put until another is tapped.
      if (!window.matchMedia("(hover: none)").matches) {
        infoWindow.hide();
      }
    });
}

function bindEditMode() {
  $(".interactive-map-item").css("display", "block").each((_, el) => {
    draggable(el, {
      onStop() {
        const coords = $(".interactive-map-item")
          .map((index, item) => ({
            index,
            left: parseInt($(item).css("left"), 10),
            top: parseInt($(item).css("top"), 10),
          }))
          .get();

        $.post("/tools/save-map-coords.php", { coords: JSON.stringify(coords) });
      },
    });
  });
}

export default function initInteractiveMap() {
  const $map = $(".interactive-map");
  const $content = $(".interactive-map-content");

  if (!$map.length || !$content.length) {
    return;
  }

  const infoWindow = createInfoWindow($map);

  positionContent($map, $content);
  makePannable($map, $content);

  bindCategoryMenu(infoWindow);
  bindRolloverMenu(infoWindow);

  if (EDIT_MODE) {
    bindEditMode();
  }

  window.addEventListener(
    "resize",
    debounce(() => positionContent($map, $content), 150),
  );
}
